// src/components/Header.jsx

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { searchPlayersByName } from '../api/snippets'; // Adjust path as needed
import { debounce } from 'lodash';
import {
    HomeIcon,
    Squares2X2Icon,
    PlusIcon,
    LockClosedIcon,
    InformationCircleIcon,
    ArrowLeftOnRectangleIcon,
    UserCircleIcon,
    ArrowPathIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';

// --- Logo Component ---
const Logo = () => (
    <Link to="/about" aria-label="About TWIF">
      <img
        src="/twifwave - notitle.png" // Ensure this path is correct in /public
        alt="The Week In Fantasy"
        className="h-10 w-auto"
      />
    </Link>
  );

// Header component
const Header = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [isLoading, setIsLoading] = useState(false); // For search input loading
  const navigate = useNavigate();
  const searchContainerRef = useRef(null);
  const { pathname } = useLocation(); // Get current pathname

  // --- Get Auth State ---
  const { session, user, profile, signOut, loading: authLoading } = useAuth();

  // --- Debounced search function ---
  const debouncedSearch = useCallback(
    debounce(async (query) => {
      if (query.length >= 3) {
        setIsLoading(true);
        try {
          const results = await searchPlayersByName(query);
          setSearchResults(results);
          setShowResults(true);
        } catch (error) {
          console.error("Search failed:", error);
          setSearchResults([]);
          setShowResults(false);
        } finally {
          setIsLoading(false);
        }
      } else {
        setSearchResults([]);
        setShowResults(false);
        setIsLoading(false);
      }
    }, 300),
    []
  );

  useEffect(() => {
    debouncedSearch(searchTerm);
    return () => {
      debouncedSearch.cancel();
    };
  }, [searchTerm, debouncedSearch]);

  // --- Click outside handler ---
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };
    if (showResults) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showResults]);

  const handleInputChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleResultClick = (playerId) => {
    setSearchTerm('');
    setSearchResults([]);
    setShowResults(false);
    navigate(`/player/${playerId}`);
  };

  // --- Logout Handler ---
  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/');
      console.log("Logout successful");
    } catch (error) {
      console.error("Logout failed:", error);
      alert(`Logout failed: ${error.message}`);
    }
  };

  // --- Render User Name Helper ---
  const renderUserName = () => {
    if (authLoading || !session) return null;
    if (user && profile) {
      if (profile.first_name && profile.last_name) return `${profile.first_name} ${profile.last_name}`;
      if (profile.first_name) return profile.first_name;
      return user.email;
    }
    if (user) return user.email;
    return null;
  };

  return (
    <header className="bg-base-100 text-base-content shadow-md p-3 sticky top-0 z-50">
      <div className="w-full flex justify-between items-center gap-1 md:gap-2 px-1 sm:px-4 lg:px-8">

        {/* Left Side: About Icon (Mobile), Home Icon (Mobile), Logo (Desktop) */}
        <div className="flex items-center gap-0.5 flex-shrink-0">
          {/* Mobile Only: About Us Icon Button */}
          <Link to="/about" className="btn btn-ghost btn-xs md:hidden" aria-label="About TWIF">
            <InformationCircleIcon className="w-6 h-6" />
          </Link>

          {/* Mobile Only: Dashboard Icon Button */}
          <Link to="/main-menu" className="btn btn-ghost btn-xs md:hidden" aria-label="Dashboard" >
            <Squares2X2Icon className="w-5 h-5" />
          </Link>

          {/* Desktop Only: Full Logo */}
          <div className="hidden md:block">
            <Logo />
          </div>
        </div>

        {/* Center: Search Bar */}
        {session && (
          <div className="flex-grow mx-1 md:mx-4 md:max-w-xl relative" ref={searchContainerRef}>
            <input
              type="text"
              placeholder="Search Players..."
              className="input text-base md:text-sm input-bordered w-full"
              value={searchTerm}
              onChange={handleInputChange}
              onFocus={() => searchTerm.length >= 3 && setShowResults(true)}
              disabled={authLoading}
            />
            {isLoading && ( <span className="loading loading-spinner loading-xs absolute right-2 top-1/2 transform -translate-y-1/2 text-primary"></span> )}
            {showResults && searchResults.length > 0 && ( <ul className="absolute mt-1 w-full bg-base-200 border border-base-300 rounded-md shadow-lg max-h-60 overflow-y-auto z-10"> {searchResults.map((player) => ( <li key={player.id} className="px-4 py-2 hover:bg-primary hover:text-primary-content cursor-pointer text-sm" onMouseDown={() => handleResultClick(player.id)}> {player.name} <span className="text-xs opacity-70">{player.position} {player.team_abbr}</span> </li> ))} </ul> )}
            {showResults && searchResults.length === 0 && !isLoading && searchTerm.length >= 3 && ( <div className="absolute mt-1 w-full bg-base-200 border border-base-300 rounded-md shadow-lg p-4 z-10 text-center text-sm opacity-70"> No players found matching "{searchTerm}". </div> )}
          </div>
        )}

        {/* Right Side: Auth Actions & Desktop Dashboard Link */}
        <div className="flex items-center gap-0.5 sm:gap-1 flex-shrink-0">

          {/* Desktop Only: Dashboard Text Link */}
          <Link
            to="/main-menu"
            className={`btn btn-ghost btn-sm hidden md:inline-flex text-sm normal-case ${pathname === '/main-menu' ? 'btn-active font-bold' : 'hover:font-bold'}`}
            aria-label="Dashboard"
            title="Dashboard"
          >
            Dashboard
          </Link>

          {/* ====== Conditional Auth Section ====== */}
          {authLoading ? (
            <span className="loading loading-spinner loading-sm mx-2"></span>
          ) : session ? (
            // --- User is LOGGED IN ---
            <>
              {/* Desktop Only: + Team Link */}
              <Link
                to="/addteam"
                className={`btn btn-ghost btn-sm hidden md:inline-flex items-center normal-case text-sm ${pathname === '/addteam' ? 'btn-active font-bold' : 'hover:font-bold'}`}
                title="Add or Sync Team"
                aria-label="Add or Sync Team"
              >
                <PlusIcon className="w-4 h-4 mr-1.5" />
                Team
              </Link>

              {/* User Link - Mobile (Icon only) */}
              <Link
                to="/user"
                className="btn btn-ghost btn-xs md:hidden" // Icon only for mobile, btn-xs for consistency
                title={"Your Teams and Profile"}
                aria-label={renderUserName() ? `View profile for ${renderUserName()}` : 'User Profile'}
              >
                <UserCircleIcon className="w-6 h-6" />
              </Link>

              {/* User Link - Desktop (Text with active highlighting) */}
              <Link
                to="/user"
                className={`btn btn-ghost btn-sm hidden md:inline-flex items-center normal-case text-sm ${pathname === '/user' ? 'btn-active font-bold' : 'hover:font-bold'}`}
                title={"Your Teams and Profile"}
                aria-label={renderUserName() ? `View profile for ${renderUserName()}` : 'User Profile'}
              >
                {renderUserName()}
              </Link>

              {/* Logout Button */}
              <button
                onClick={handleLogout}
                className="btn btn-error btn-xs text-white hover:bg-error/10"
                aria-label="Logout"
                title="Logout"
              >
                <span className="md:hidden"><ArrowLeftOnRectangleIcon className="w-5 h-5" /></span>
                <span className="hidden md:inline text-sm">Logout</span>
              </button>
            </>
          ) : (
            // --- User is LOGGED OUT ---
            <Link
              to="/auth"
              className="btn btn-ghost md:btn-primary btn-xs md:shadow-md md:transition md:hover:shadow-lg md:active:shadow-sm md:active:translate-y-px"
              aria-label="Login or Signup"
            >
              <span className="md:hidden"><LockClosedIcon className="w-5 h-5" /></span>
              <span className="hidden md:inline text-sm">Login/Signup</span>
            </Link>
          )}
          {/* ====== End Conditional Auth Section ====== */}

        </div>
      </div>
    </header>
  );
};

export default Header;